import { h, FunctionComponent } from 'preact';
import { useEffect } from 'preact/hooks';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { formatCustomerNames } from '~src/utils/helpers';
import { trackHandler, trackPageMount } from '~src/utils/tracking';
import CurrencyText from '~src/domains/common/components/CurrencyText';
import { Page } from '~src/domains/common/enums/api';
import {
  customerNamesSelector,
  firstPaymentDateSelector,
  last4Selector,
  loanOfferSelectionSelector,
  loanOffersSelector,
  localeSelector,
  isOnboardSpendSelector,
  brandSelector,
} from '~src/domains/common/state/commonSelectors';
import { LoanOfferType } from '~src/domains/common/types/offers';
import * as styles from '~src/styles/domains/pay/PayPage.module.scss';
import SimpleButton from '../common/components/Button';

const PayPage: FunctionComponent = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const brand = useSelector(brandSelector);
  const loanOffers = useSelector(loanOffersSelector);
  const loanOfferSelection = useSelector(loanOfferSelectionSelector);
  const customerNames = useSelector(customerNamesSelector);
  const last4 = useSelector(last4Selector);
  const locale = useSelector(localeSelector);
  const firstPaymentDate = useSelector(firstPaymentDateSelector);

  /** Returns localized date from a given datestring.
   * E.g. 1963-04-07 -> Apr 07, 1963
   * @param {string} date - Date.
   */
  const dateParse = (date: string) => {
    let [year, parsedMonth, parsedDay] = date.split('-');
    const monthString = t(`common:time.months.${parseInt(parsedMonth)}`);

    if (parsedDay && monthString && year) {
      return (
        <Trans
          t={t}
          i18nKey='pay:date'
          values={{ day: parsedDay, month: monthString, year: year }}
        />
      );
    } else {
      return 'MM/DD/YYYY';
    }
  };

  const closePage = () => {
    window.open('', '_self', ''); // Needed since scripts may close only the windows that were opened by them
    window.close();
  };

  const offer = loanOffers.find(
    offer => offer.offer_id === loanOfferSelection,
  ) as LoanOfferType;
  const { firstName } = formatCustomerNames(customerNames);
  const { pay_today, monthly_payment_amount } = offer;
  const downPayment = (
    <CurrencyText
      value={pay_today}
      data-auto='pay-today-amount'
      locale={locale}
    />
  );
  const monthlyPayment = (
    <CurrencyText value={monthly_payment_amount} locale={locale} />
  );
  const formattedFirstPaymentDate =
    firstPaymentDate && dateParse(firstPaymentDate);
  const isZeroDown = pay_today === 0;
  const isOnboardSpend = useSelector(isOnboardSpendSelector);

  useEffect(() => {
    dispatch(trackPageMount(Page.PAY));
    dispatch(trackHandler('is-confirmed-response'));
  }, []);

  return (
    <div className={styles['pay-wrapper']}>
      <div className={`header-txt ${styles['pay-header']}`}>
        <Trans t={t} i18nKey={'pay:header'} values={{ firstName }} />
      </div>
      <div className={styles['pay-body']}>
        {!isZeroDown && (
          <Trans
            t={t}
            i18nKey={
              last4 ? 'pay:downPayment.last4' : 'pay:downPayment.noLast4'
            }
            components={{ downPayment, strong: <strong data-auto='last4' /> }}
            data-auto='last4'
            values={{ last4 }}
          />
        )}
        {isZeroDown && (
          <Trans
            t={t}
            i18nKey={
              last4 ? 'pay:noDownPayment.last4' : 'pay:noDownPayment.noLast4'
            }
            components={{ downPayment, strong: <strong data-auto='last4' /> }}
            values={{ last4 }}
          />
        )}
        <Trans
          t={t}
          i18nKey={`pay:${brand.toLowerCase()}.starting-next-month`}
          components={{
            monthlyPayment,
            dueDate: formattedFirstPaymentDate,
            firstPaymentDateAuto: <strong data-auto='first-payment-date' />,
            monthlyPaymentAuto: <strong data-auto='monthly-payment-amount' />,
          }}
        />
        <Trans
          t={t}
          i18nKey={`pay:${brand.toLowerCase()}.manage-loan`}
          components={[
            <a
              href={t(`pay:${brand.toLowerCase()}.payLink`)}
              target='_blank'
              rel='no-opener'
            />,
          ]}
        />
        <Trans t={t} i18nKey={`pay:${brand.toLowerCase()}.thank-you`} />
      </div>
    </div>
  );
};

export default PayPage;
